import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import 'firebase/compat/auth';
import {functions} from 'firebaseApp';
import {httpsCallable} from 'firebase/functions';
import {
  getWebappBrowserTestQueryState,
  shouldHonorWebappBrowserTestQueryContract,
} from 'browserTestHarness';
import {clockRetryDelayMs, fetchServerTimeDelta} from './serverClock';
import {serverActionTimeMs} from './serverActionTime';

export const ONE_SECOND_MS = 1000;
export const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
export const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;
export const ONE_WEEK_MS = 7 * ONE_DAY_MS;

/** How often `TimeProvider` checks whether the device clock has jumped. */
export const CLOCK_JUMP_CHECK_INTERVAL_MS = 10 * ONE_SECOND_MS;

/**
 * Disagreement between wall-clock and monotonic elapsed time, over one check
 * interval, at or above which `TimeProvider` re-measures the server delta.
 */
export const CLOCK_JUMP_THRESHOLD_MS = 2 * ONE_SECOND_MS;

/**       
 * This context provides the server time to the client.
 * Caution: because of internet delays it could be incorrect up to
 * about 1 second. Do not use it for anything which needs more precise
 * timing.
 *
 * Stores the server time in the `serverDelta variable. It is the difference
 * in ms between the server time and the client time.
 */
export interface TimeContextType {
  /**
   * 
   * @returns 
   */
  getServerTimeMs: () => number | undefined;
  serverTimeDelta: number | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  hasEditedCodeSinceUpdateRef?: any;
  /**
   * True until a server clock read succeeds. A failed read leaves it true
   * while the provider keeps reading on a backoff; it turns false together
   * with `serverTimeDelta` becoming known.
   */
  serverTimeLoading: boolean;
  /**
   * Resolves to the server's current time in epoch ms once the server clock
   * is known (at once if it already is). Never resolves to the device clock;
   * if the server clock can't be read it never resolves, so callers that must
   * not wait forever add their own timeout. Omitted by providers built for
   * tests.
   */
  waitForServerTimeMs?: () => Promise<number>;
}

export function useServerTime(refreshRate: number = 1000): number | undefined {
  const {serverTimeDelta} = useContext(TimeContext);
  const [serverTimeMs, setServerTimeMs] = useState(Date.now() + serverTimeDelta);

  // once we have the delta, start updating the time
  useEffect(() => {
    if (serverTimeDelta === undefined) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setServerTimeMs(Date.now() + serverTimeDelta);
    const interval = setInterval(() => {
      setServerTimeMs(Date.now() + serverTimeDelta);
    }, refreshRate); // refresh every second
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [serverTimeDelta]);

  return serverTimeMs;
};

export function useServerTimeOnce(): number | undefined {
  const {getServerTimeMs} = useContext(TimeContext);
  const [serverTimeMs, setServerTimeMs] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (getServerTimeMs === undefined) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setServerTimeMs(getServerTimeMs());
  }, [getServerTimeMs]);

  return serverTimeMs;
};

/**
 * The server time for an event handler that stores or decides with it, such
 * as a submission stamp or a deadline check.
 *
 * @return A function resolving to the server time in epoch milliseconds. It
 *   waits briefly while the server clock loads (see `serverActionTimeMs`) and
 *   resolves to `undefined` if it stays unknown; the caller then writes
 *   nothing rather than use the device clock. The function changes identity
 *   only once, when the server clock offset becomes known.
 * @throws Never.
 */
export function useServerActionTime(): () => Promise<number | undefined> {
  const {getServerTimeMs, waitForServerTimeMs} = useContext(TimeContext);
  return useCallback(
    () => serverActionTimeMs({getServerTimeMs, waitForServerTimeMs}),
    [getServerTimeMs, waitForServerTimeMs],
  );
}

const defaultData: TimeContextType = {
  getServerTimeMs: undefined,
  serverTimeDelta: undefined,
  hasEditedCodeSinceUpdateRef: undefined,
  serverTimeLoading: true,
};

// TODO: Copy this to the server
// eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
type GetServerTimeRequest = void;
interface GetServerTimeResponse {
  serverTime: number;
}

export const TimeContext = createContext<TimeContextType>(defaultData);
export const TimeProvider = ({children}) => {
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [serverTimeDelta, setServerDelta] = useState<number | undefined>(
    undefined,
  );
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [serverTimeLoading, setServerLoading] = useState(true);
  const hasEditedCodeSinceUpdateRef = useRef(undefined);
  // Settles once the offset is first known; waitForServerTimeMs then reads
  // the latest offset from latestDeltaRef, which a re-measure updates.
  const latestDeltaRef = useRef<number>();
  const deltaWaiterRef = useRef<{promise: Promise<number>; resolve: (delta: number) => void}>();
  if (deltaWaiterRef.current === undefined) {
    let resolve: (delta: number) => void = () => {};
    const promise = new Promise<number>(settle => {
      resolve = settle;
    });
    deltaWaiterRef.current = {promise, resolve};
  }

  useEffect(() => {
    // When the harness injects a frozen "now" via `?testTime=<iso>`, skip
    // the getServerTime callable and derive the delta from the injected
    // value. The delta is snapshotted at mount time, so downstream
    // `Date.now() + serverTimeDelta` math continues to tick forward at
    // wall-clock speed starting from the frozen instant — tests that care
    // about absolute freeze should not rely on `useServerTime` ticking
    // past the injected instant, but all existing consumers want "roughly
    // this moment" rather than "exactly this instant forever."
    if (shouldHonorWebappBrowserTestQueryContract()) {
      const testTimeIso = getWebappBrowserTestQueryState().resolvedPlan?.testTimeIso;
      if (testTimeIso) {
        const frozenServerTime = new Date(testTimeIso).getTime();
        const clientTime = Date.now();
        // Mirrors the `getServerTime().then(...)` branch below; both of
        // them intentionally set state from within the mount effect so
        // downstream consumers get a concrete delta before the first
        // render pass that reads it.
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- see comment above
        setServerDelta(frozenServerTime - clientTime);
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- see comment above
        setServerLoading(false);
        latestDeltaRef.current = frozenServerTime - clientTime;
        deltaWaiterRef.current?.resolve(frozenServerTime - clientTime);
        return;
      }
    }

    const getServerTime = httpsCallable<
      GetServerTimeRequest,
      GetServerTimeResponse
    >(functions, 'getServerTime');
    let isStopped = false;
    let isReading = false;
    let isReadRequested = false;
    let isReadOwed = true;
    let failures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // While a read is owed (none has succeeded yet, or the device clock
    // jumped since the last one), the provider reads on a backoff, when the
    // connection returns, and when the tab is shown, until one succeeds.
    // Server time stays unknown until the first success (never the
    // device's): pages under CoursePage assume a known server time, and
    // several write or unlock on NaN. A failed re-measure keeps the previous
    // offset.
    const readClock = async (): Promise<void> => {
      if (isStopped) {
        return;
      }
      if (isReading) {
        isReadRequested = true;
        return;
      }
      isReading = true;
      isReadRequested = false;
      clearTimeout(retryTimer);
      const serverDelta = await fetchServerTimeDelta({
        callServerTime: async () => (await getServerTime()).data.serverTime,
      });
      isReading = false;
      if (isStopped) {
        return;
      }
      if (serverDelta === undefined) {
        failures += 1;
        console.warn('[TimeContext] server clock unavailable; retrying', {failures});
        // A reconnect or tab show during the failed round is a fresh chance
        // the network is back, so it skips the backoff.
        if (isReadRequested) {
          void readClock();
          return;
        }
        retryTimer = setTimeout(() => void readClock(), clockRetryDelayMs(failures));
        return;
      }
      isReadOwed = false;
      failures = 0;
      latestDeltaRef.current = serverDelta;
      setServerDelta(serverDelta);
      setServerLoading(false);
      deltaWaiterRef.current?.resolve(serverDelta);
    };
    const readIfOwed = (): void => {
      if (isReadOwed) {
        void readClock();
      }
    };

    // The offset is only valid while the device clock runs steadily. A manual
    // change, an NTP correction, or a sleep that pauses the monotonic clock
    // makes wall-clock and monotonic elapsed time disagree, so re-measure.
    let lastWallMs = Date.now();
    let lastMonotonicMs = performance.now();
    const checkClockJump = (): void => {
      const wallMs = Date.now();
      const monotonicMs = performance.now();
      const driftMs = wallMs - lastWallMs - (monotonicMs - lastMonotonicMs);
      lastWallMs = wallMs;
      lastMonotonicMs = monotonicMs;
      if (Math.abs(driftMs) >= CLOCK_JUMP_THRESHOLD_MS) {
        isReadOwed = true;
        void readClock();
      }
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      checkClockJump();
      readIfOwed();
    };
    const clockJumpCheck = setInterval(checkClockJump, CLOCK_JUMP_CHECK_INTERVAL_MS);
    window.addEventListener('online', readIfOwed);
    document.addEventListener('visibilitychange', onVisibilityChange);
    void readClock();
    return () => {
      isStopped = true;
      clearTimeout(retryTimer);
      clearInterval(clockJumpCheck);
      window.removeEventListener('online', readIfOwed);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const getServerTimeMs = useCallback((): number | undefined => {
    if (serverTimeDelta === undefined) {
      return undefined;
    }
    return Date.now() + serverTimeDelta;
  }, [serverTimeDelta]);

  const waitForServerTimeMs = useCallback(async (): Promise<number> => {
    const firstDelta = await (deltaWaiterRef.current?.promise ?? new Promise<number>(() => {}));
    return Date.now() + (latestDeltaRef.current ?? firstDelta);
  }, []);

  return (
    <TimeContext.Provider
      value={{
        getServerTimeMs,
        serverTimeDelta,
        hasEditedCodeSinceUpdateRef,
        serverTimeLoading,
        waitForServerTimeMs,
      }}
    >
      {children}
    </TimeContext.Provider>
  );
};
